import { CellValueChangedEvent } from 'ag-grid-community';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  KpiCompanySettingsGridRow,
  toKpiCompanySettingsPayload,
} from '../../../../schemas/KpiCollectionCompanySettings.schema';
import { useBulkUpdateKpiCollectionCompanySettings } from '../state/kpi-collection-state';

type SaveStatus = 'initial' | 'pending' | 'saving' | 'saved' | 'error';

export interface SaveResult {
  success: boolean;
  failedCompanyIds?: number[];
}

export function useBulkSaveKpiCompanySettingsGridData() {
  const [pendingChanges, setPendingChanges] = useState<Map<number, KpiCompanySettingsGridRow>>(
    () => new Map()
  );
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('initial');

  const { mutateAsync: bulkUpdateKpi } = useBulkUpdateKpiCollectionCompanySettings({
    invalidateOnSuccess: false,
  });

  const pendingChangesRef = useRef(pendingChanges);
  pendingChangesRef.current = pendingChanges;

  const onCellValueChanged = useCallback((event: CellValueChangedEvent<KpiCompanySettingsGridRow>) => {
    const { data } = event;
    if (!data?.companyId) {
      return;
    }

    // Shallow clone to create a new object reference on each edit.
    // This allows onSave to detect if a row was re-edited during save
    // by comparing object references (ag-Grid may reuse the same object).
    setPendingChanges((prev) => new Map(prev).set(data.companyId, { ...data }));
    setSaveStatus('pending');
  }, []);

  const onSave = useCallback(async (): Promise<SaveResult> => {
    const changes = pendingChangesRef.current;
    if (changes.size === 0) {
      return { success: true };
    }

    // Snapshot the exact rows we're saving. Used to detect if a row was re-edited
    // during save by comparing object references (see onCellValueChanged comment).
    const savedRows = new Map(changes);

    setSaveStatus('saving');

    try {
      const result = await bulkUpdateKpi([...changes.values()].map(toKpiCompanySettingsPayload));

      if (result?.errors?.length) {
        const failedIds = new Set(result.errors.map((e) => e.companyId));
        setPendingChanges((current) => {
          const remaining = new Map<number, KpiCompanySettingsGridRow>();
          for (const [id, row] of current) {
            // Keep if: failed, OR re-edited during save (different reference), OR new row
            if (failedIds.has(id) || savedRows.get(id) !== row) {
              remaining.set(id, row);
            }
          }
          return remaining;
        });
        setSaveStatus('error');
        return { success: false, failedCompanyIds: [...failedIds] };
      }

      // Clear only rows that haven't been re-edited during save
      setPendingChanges((current) => {
        const remaining = new Map<number, KpiCompanySettingsGridRow>();
        for (const [id, row] of current) {
          // Keep if row was re-edited (different reference) or is a new row
          if (savedRows.get(id) !== row) {
            remaining.set(id, row);
          }
        }
        return remaining;
      });
      setSaveStatus('saved');
      return { success: true };
    } catch (error) {
      setSaveStatus('error');
      return { success: false };
    }
  }, [bulkUpdateKpi, setPendingChanges, setSaveStatus]);

  const hasUnsavedChanges = pendingChanges.size > 0;
  const isSaving = saveStatus === 'saving';

  return useMemo(
    () => ({
      onCellValueChanged,
      onSave,
      saveStatus,
      hasUnsavedChanges,
      isSaving,
    }),
    [saveStatus, hasUnsavedChanges, isSaving, onCellValueChanged, onSave]
  );
}
